import React, { useState } from 'react';
import { Modal, Button, Row, Col, Badge } from 'react-bootstrap';
import {
  Shirt, Edit, X, Clock, DollarSign, Tag,
  Image as ImageIcon, Calendar, User, Star,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import modelService from '../../../services/modelService';

/**
 * ✅ ATELIER: Modal de détail d'un modèle.
 *
 * ✅ MODIFIÉ:
 *   - Plus de section "tissu"
 *   - Affiche la galerie complète d'images :
 *       * Image principale en grand
 *       * Vignettes cliquables pour changer d'image
 *       * Clic sur l'image principale → zoom plein écran
 */
const ModelDetailModal = ({
  show,
  onHide,
  model,
  onEdit,
  isManager
}) => {

  // ✅ MODIFIÉ: index de l'image actuellement affichée en grand
  const [currentIndex, setCurrentIndex] = useState(0);
  // Image zoomée plein écran
  const [zoomedImage, setZoomedImage] = useState(null);

  // Reset à l'ouverture / changement de modèle
  React.useEffect(() => {
    if (show) {
      setCurrentIndex(0);
      setZoomedImage(null);
    }
  }, [show, model?.id]);

  if (!model) return null;

  // ✅ NOUVEAU: construction de la liste d'images à afficher
  // Priorité : galerie (model.images), sinon fallback sur model.imageUrl
  const galleryImages = Array.isArray(model.images) && model.images.length > 0
    ? model.images
    : (model.imageUrl
        ? [{ id: 'legacy', imageUrl: model.imageUrl, isPrimary: true }]
        : []);

  const hasImages = galleryImages.length > 0;
  const currentImage = hasImages ? galleryImages[currentIndex] : null;
  const currentImageUrl = currentImage
    ? modelService.buildImageUrl(currentImage.imageUrl)
    : null;

  const handleEditClick = () => {
    onHide();
    setTimeout(() => onEdit?.(model), 150);
  };

  const goToPrevious = () => {
    setCurrentIndex(prev =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex(prev =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center gap-2">
            <Shirt size={20} className="text-primary" />
            Détails du modèle
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Nom + catégorie */}
          <div className="mb-4">
            <h4 className="mb-2">{model.name}</h4>
            {model.category && (
              <Badge bg="primary" className="me-2">
                <Tag size={12} className="me-1" />
                {model.category}
              </Badge>
            )}
            {!model.isActive && (
              <Badge bg="secondary">Désactivé</Badge>
            )}
          </div>

          {/* Description */}
          {model.description && (
            <div className="mb-4">
              <h6 className="text-muted small text-uppercase mb-2">Description</h6>
              <p className="mb-0">{model.description}</p>
            </div>
          )}

          {/* ✅ NOUVEAU: Galerie d'images */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-muted small text-uppercase mb-2">
              <ImageIcon size={14} className="text-primary" />
              Photos
              {galleryImages.length > 0 && (
                <Badge bg="light" text="dark">
                  {galleryImages.length}
                </Badge>
              )}
            </h6>

            {!hasImages ? (
              <div className="gallery-empty">
                <ImageIcon size={40} />
                <small className="mt-2 text-muted">Aucune photo</small>
              </div>
            ) : (
              <div className="gallery-viewer">
                {/* Image principale affichée */}
                <div
                  className="main-image-frame"
                  onClick={() => setZoomedImage({
                    url: currentImageUrl,
                    label: model.name
                  })}
                >
                  <img
                    src={currentImageUrl}
                    alt={`${model.name} ${currentIndex + 1}/${galleryImages.length}`}
                  />

                  {/* Badge image principale */}
                  {currentImage?.isPrimary && (
                    <div className="main-primary-badge">
                      <Star size={12} fill="currentColor" />
                      <span className="ms-1">Principale</span>
                    </div>
                  )}

                  {/* Compteur */}
                  {galleryImages.length > 1 && (
                    <div className="image-counter">
                      {currentIndex + 1} / {galleryImages.length}
                    </div>
                  )}

                  {/* Boutons navigation (si plusieurs images) */}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="nav-btn nav-btn-prev"
                        onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                        title="Image précédente"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        type="button"
                        className="nav-btn nav-btn-next"
                        onClick={(e) => { e.stopPropagation(); goToNext(); }}
                        title="Image suivante"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}

                  <div className="image-zoom-hint">
                    <ImageIcon size={14} />
                    <span className="ms-1 small">Cliquer pour agrandir</span>
                  </div>
                </div>

                {/* Vignettes (si plus d'une image) */}
                {galleryImages.length > 1 && (
                  <div className="thumbnails-row">
                    {galleryImages.map((img, idx) => {
                      const thumbUrl = modelService.buildImageUrl(img.imageUrl);
                      return (
                        <button
                          key={img.id}
                          type="button"
                          className={`thumbnail ${idx === currentIndex ? 'active' : ''}`}
                          onClick={() => setCurrentIndex(idx)}
                          title={img.isPrimary ? 'Image principale' : `Photo ${idx + 1}`}
                        >
                          <img src={thumbUrl} alt="" />
                          {img.isPrimary && (
                            <div className="thumbnail-primary">
                              <Star size={10} fill="currentColor" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Infos clés */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="info-card">
                <div className="info-card-icon bg-success-subtle text-success">
                  <DollarSign size={20} />
                </div>
                <div>
                  <div className="info-card-label">Prix de base</div>
                  <div className="info-card-value">
                    {modelService.formatPrice(model.basePrice)}
                  </div>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="info-card">
                <div className="info-card-icon bg-primary-subtle text-primary">
                  <Clock size={20} />
                </div>
                <div>
                  <div className="info-card-label">Durée de confection</div>
                  <div className="info-card-value">
                    {modelService.formatEstimatedDays(model.estimatedDays)}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Métadonnées */}
          <Row className="g-3">
            {model.createdByName && (
              <Col md={6}>
                <div className="meta-row">
                  <User size={14} className="text-muted me-2" />
                  <span className="text-muted small">Créé par</span>
                  <strong className="ms-auto small">{model.createdByName}</strong>
                </div>
              </Col>
            )}
            {model.createdAt && (
              <Col md={6}>
                <div className="meta-row">
                  <Calendar size={14} className="text-muted me-2" />
                  <span className="text-muted small">Créé le</span>
                  <strong className="ms-auto small">{formatDate(model.createdAt)}</strong>
                </div>
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            <X size={16} className="me-1" />
            Fermer
          </Button>
          {isManager && (
            <Button variant="primary" onClick={handleEditClick}>
              <Edit size={16} className="me-1" />
              Modifier
            </Button>
          )}
        </Modal.Footer>

        <style>{`
          .gallery-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            background: #f8f9fa;
            border-radius: 8px;
            color: #adb5bd;
          }

          .gallery-viewer {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .main-image-frame {
            position: relative;
            width: 100%;
            aspect-ratio: 4 / 3;
            border-radius: 8px;
            overflow: hidden;
            background: #f8f9fa;
            cursor: zoom-in;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .main-image-frame img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            background: #f8f9fa;
            display: block;
          }

          .main-primary-badge {
            position: absolute;
            top: 12px;
            left: 12px;
            background: #ffc107;
            color: #212529;
            border-radius: 14px;
            padding: 4px 10px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
            z-index: 3;
          }

          .image-counter {
            position: absolute;
            top: 12px;
            right: 12px;
            background: rgba(0, 0, 0, 0.65);
            color: white;
            padding: 4px 10px;
            border-radius: 14px;
            font-size: 0.75rem;
            font-weight: 500;
            backdrop-filter: blur(4px);
            z-index: 3;
          }

          .nav-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.9);
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #495057;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            transition: all 0.15s ease;
            z-index: 3;
          }

          .nav-btn:hover {
            background: white;
            color: #212529;
            transform: translateY(-50%) scale(1.05);
          }

          .nav-btn-prev {
            left: 12px;
          }

          .nav-btn-next {
            right: 12px;
          }

          .image-zoom-hint {
            position: absolute;
            bottom: 12px;
            right: 12px;
            background: rgba(0, 0, 0, 0.65);
            color: white;
            padding: 4px 10px;
            border-radius: 14px;
            display: inline-flex;
            align-items: center;
            backdrop-filter: blur(4px);
            opacity: 0;
            transition: opacity 0.15s ease;
            z-index: 3;
          }

          .main-image-frame:hover .image-zoom-hint {
            opacity: 1;
          }

          .thumbnails-row {
            display: flex;
            gap: 0.5rem;
            overflow-x: auto;
            padding: 0.25rem 0;
            scrollbar-width: thin;
          }

          .thumbnail {
            position: relative;
            flex: 0 0 70px;
            height: 70px;
            border-radius: 6px;
            overflow: hidden;
            border: 2px solid transparent;
            background: #f8f9fa;
            cursor: pointer;
            padding: 0;
            transition: all 0.15s ease;
          }

          .thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .thumbnail.active {
            border-color: #0d6efd;
            box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
          }

          .thumbnail-primary {
            position: absolute;
            top: 2px;
            right: 2px;
            background: #ffc107;
            color: #212529;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .info-card {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem;
            border-radius: 8px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
          }

          .info-card-icon {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .info-card-label {
            font-size: 0.75rem;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .info-card-value {
            font-weight: 600;
            color: #1f2937;
            font-size: 1rem;
          }

          .meta-row {
            display: flex;
            align-items: center;
            padding: 0.5rem 0.75rem;
            background: #f8f9fa;
            border-radius: 6px;
          }
        `}</style>
      </Modal>

      {/* Modal de zoom plein écran */}
      <Modal
        show={!!zoomedImage}
        onHide={() => setZoomedImage(null)}
        size="lg"
        centered
        contentClassName="bg-transparent border-0"
      >
        <Modal.Body className="text-center p-0 position-relative">
          {zoomedImage && (
            <>
              <div className="zoom-label">
                <Shirt size={14} className="me-1" />
                {zoomedImage.label}
              </div>
              <img
                src={zoomedImage.url}
                alt={zoomedImage.label}
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }}
              />
            </>
          )}
        </Modal.Body>

        <style>{`
          .zoom-label {
            position: absolute;
            top: 16px;
            left: 16px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            z-index: 10;
          }
        `}</style>
      </Modal>
    </>
  );
};

export default ModelDetailModal;